import axios from 'axios';
import { apiClient } from './client';
import type {
  ApprovalBoxCounts,
  ApprovalDelegatesResponse,
  ApprovalDocumentDetail,
  ApprovalListResponse,
  DocumentView,
  InvolvedStatus,
} from '../types/approval';

/**
 * axios 에러 → 사용자에게 표시할 메시지 추출.
 * backend 의 `{ code, message }` 응답을 우선, 없으면 axios 기본 메시지.
 *
 * 사용:
 *   try { await approveDocument(...) }
 *   catch (err) { setError(extractErrorMessage(err)); }
 */
export function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { code?: string; message?: string } | undefined;
    if (data?.message) return data.message;
    if (data?.code) return data.code;
  }
  if (err instanceof Error) return err.message;
  return '알 수 없는 오류가 발생했습니다.';
}

/**
 * 서버가 준 **`code`** 를 꺼낸다 — 화면이 「무엇을 할 수 있는지」를 코드로 가를 때 쓴다.
 *
 * ## ⛔ `error` 칸은 «코드가 아니다»
 *
 * chat-server 는 `code` 를 쓴다(실측: 1,910곳 대 `error` 82곳). 그리고 **Fastify 기본 404 의
 * 본문은 `{ message, error:'Not Found', statusCode }`** 라, `error` 를 코드로 읽으면
 * 「라우트가 없다」가 «코드 있는 404» 로 보여 오판한다.
 *
 * ★ **판정은 한 곳에 둔다** — `api/minutes-send.ts` 의 `serverCodeOf` 가 이것을 부른다.
 *   같은 일을 두 글자로 두면 어느 날 한쪽만 고쳐진다(이 레포가 겪은 그 병이다).
 */
export function extractErrorCode(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null;
  const data = err.response?.data as { code?: unknown } | undefined;
  return typeof data?.code === 'string' ? data.code : null;
}

/**
 * Approval API client — backend `apps/chat-server/src/modules/approval/approval.routes.ts` 와 1:1.
 *
 * 응답 status code (backend 정책):
 *   200 OK   — 정상 전이.
 *   201      — 신규 생성 (POST /documents, POST /delegates).
 *   404      — 미존재 / 비-관계자 (NOT_RELATED — 정보 누출 차단).
 *   400      — 검증 실패 (DRAFTER_IN_LINE / CROSS_TENANT_APPROVER 등).
 *   403      — DRAFTER_SELF_APPROVAL (위임 chain 통한 self-approval 차단).
 *   409      — race / 이미 처리됨 (LINE_ALREADY_PROCESSED).
 *   422      — INVALID_STATE_TRANSITION (status echo 없음).
 */

export async function listDocuments(
  view: DocumentView,
  // `q` (2026-09-14 · 계약 §20.2) — 제목 · 문서번호 · 기안자 이름 서버 검색(최대 100자). 없거나 공백이면 종전과 같다.
  params?: { cursor?: string; limit?: number; q?: string },
): Promise<ApprovalListResponse> {
  const { data } = await apiClient.get<ApprovalListResponse>('/approval/documents', {
    params: { view, ...params },
  });
  // 방어 가드(additive) — items 가 배열이 아닌 비정형 응답에도 소비처 map 크래시 차단.
  return { ...data, items: Array.isArray(data?.items) ? data.items : [] };
}

/**
 * 결재함 진행 중 · 완료 · 반려 목록 (2026-09-14 · 계약 §20.3 `view=involved&status=`) — 함별 건수 `inProgress · approved · rejected` 와 **같은 조건**.
 * ★ 내 차례인 진행 중 문서도 들어 있다(한 줄의 `waiting.mine`). `q` · cursor 는 다른 문서함과 같다.
 */
export async function listInvolvedDocuments(
  status: InvolvedStatus,
  params?: { cursor?: string; limit?: number; q?: string },
): Promise<ApprovalListResponse> {
  const { data } = await apiClient.get<ApprovalListResponse>('/approval/documents', {
    params: { view: 'involved', status, ...params },
  });
  return { ...data, items: Array.isArray(data?.items) ? data.items : [] };
}

/** 함별 건수 (2026-09-14 · 계약 §20.3) — 로그인만 필요 · 나와 내 기관으로만 센다. */
export async function getDocumentCounts(): Promise<ApprovalBoxCounts> {
  const { data } = await apiClient.get<{ counts: ApprovalBoxCounts }>('/approval/documents/counts');
  return data.counts;
}

export async function getDocument(id: string): Promise<ApprovalDocumentDetail> {
  const { data } = await apiClient.get<ApprovalDocumentDetail>(`/approval/documents/${id}`);
  return data;
}

export interface CreateDocumentBody {
  title: string;
  type: string;
  content: string;
  contentType?: 'HTML' | 'TEXT';
  isUrgent?: boolean;
  isPublic?: boolean;
  saveAsDraft?: boolean;
  approverIds: string[];
  /**
   * 참조자 (근태 5단계, 2026-08-24) — **승인 대상이 아니다.** 진행을 막지 않고 결재선 뒤에
   * `REFER` 로 붙는다. 옛 서버는 이 칸을 모르지만 `additionalProperties:false` 라 400 이 나므로,
   * 호출부는 **참조자가 있을 때만** 실어 보낸다.
   */
  referrerIds?: string[];
  /**
   * 첨부 (결재 A2, 2026-08-24) — **fileId 만** 보낸다. 이름·크기는 서버가 파일 행에서 읽는다.
   *
   * ★ 옛 서버는 이 칸을 모르고 `additionalProperties:false` 라 400 이 난다 — 호출부는
   *   **첨부가 있을 때만** 실어 보낸다(참조자와 같은 규약).
   */
  attachments?: Array<{ fileId: string }>;
  /**
   * 이 문서의 **크기** — 일수(휴가) 또는 금액(지출결의서). 전결 판정에 쓴다 (결재 A3).
   * 옛 서버는 모르는 칸이라 **값이 있을 때만** 싣는다.
   */
  measureValue?: number;
  /**
   * 이 기안이 **어느 회의록에서 왔나** (AI 회의록 보내기 · 2026-09-22).
   *
   * ★ 이 칸을 실으면 서버가 **회의록의 「보낸 이력」에 한 줄을 적는다.** 안 실으면 기안은 그대로
   *   올라가고 **이력만 안 남는다** — 그래서 회의록에서 넘어온 기안만 싣는다.
   * ⛔ 옛 서버는 모르는 칸이라 `additionalProperties:false` 로 **400** 이다(참조자·첨부와 같은 규약).
   *   ⚠ 그리고 **지금 운영이 옛 판이다** — 그래서 `createDocument` 가 그 400 을 받아
   *     **이 칸만 빼고 한 번 다시 올린다**(아래 주석). 기안이 안 올라가는 것이 더 나쁘다.
   * ⚠ 서버 스키마는 `maxLength: 64` 다 — 잘못된 값을 실으면 400 이다.
   */
  minutesId?: string;
  /**
   * 기안부서 · 부서 결재함 (2026-09-07 · 시안 A1) — **안 보내는 것이 기본이다**(미지정).
   *
   * ★★★ 전사결재함과 보존연한은 **여기 없다.** 고르는 값이 아니라 양식에서 파생되고
   *    서버가 박는다 — 화면이 보내면 같은 값을 두 자리에서 정하게 된다.
   * ★ 옛 서버는 모르는 칸이라 **있을 때만** 싣는다(참조자·첨부와 같은 규약).
   */
  draftDivisionId?: string;
  draftTeamId?: string;
  deptBoxId?: string;
}

/**
 * 기안이 근태에 무엇을 남겼나 (2026-09-01 · 파이프라인 하네스 판 C).
 *
 * ★ `made:false` + `reason` 이면 **문서는 올라갔는데 근태에는 안 들어갔다** — 서식이 근태
 *   재료로 안 읽히는 경우다. 조용히 넘어가면 사람은 반영된 줄 알고, 연차는 그대로 남는다.
 * ★ `reason:null` 은 **근태와 무관한 종류**라 화면이 아무 말도 하지 않는다.
 * ★ 옛 서버는 이 칸을 안 준다 — 그때는 `undefined` 라 역시 아무 말도 안 한다.
 */
export interface HrRequestOutcomeDto {
  made: boolean;
  table?: string;
  recordId?: string;
  reason: string | null;
}

/**
 * 기안이 **지출 집행**에 무엇을 남겼나 (2026-09-04 · 예산 판 B1·B3).
 *
 * ★★ `reason` 과 `notes` 는 **뜻이 다르다**:
 *    `reason` = **못 만들었다**(지출 기록 자체가 없다) · `notes` = **만들었는데 예산에 못 붙었다**.
 *    둘을 한 칸에 담으면 화면이 「실패」로 읽는다 — 예산 없이 올리는 것은 실패가 아니다.
 * ★ 옛 서버는 이 칸을 안 준다 → `undefined` 라 화면이 아무 말도 안 한다.
 */
export interface ExpenseOutcomeDto {
  made: boolean;
  table?: string;
  recordId?: string;
  reason: string | null;
  notes?: string[];
}

/**
 * 기안 종류가 **못 쓰는 것**일 때의 코드 둘 (2026-09-22 · #1626) — 둘 다 400 이고 `code` 로 온다.
 *
 * ## ⛔ 뭉치지 않는다 — **고치는 방법이 다르다**
 *
 * ```
 * UNKNOWN_DOC_TYPE   그런 종류가 «없다»(오타·옛 앱) — 사용자가 할 수 있는 것이 없다
 * INACTIVE_DOC_TYPE  회사가 «껐다»              — ★ 관리자가 켜면 된다
 * ```
 * 뭉치면 화면이 「없는 종류입니다」라고만 말하고 사용자는 **관리자에게 물을 생각을 못 한다**
 * (서버 주석이 같은 말을 적어 뒀다 — `approval.routes.ts:602-607`).
 */
export const UNKNOWN_DOC_TYPE_CODE = 'UNKNOWN_DOC_TYPE';
export const INACTIVE_DOC_TYPE_CODE = 'INACTIVE_DOC_TYPE';

/**
 * `minutesId` 400 을 **다시 올리지 않을** 코드 — 그 칸과 무관한 «업무 거절»이다.
 *
 * ★ 아는 것만 막는다. 모르는 400 은 종전처럼 다시 올린다 — 이 목록이 낡으면 **왕복이 한 번 늘 뿐**이고,
 *   반대로 넓게 막으면 **기안이 안 올라간다.** 틀리는 방향을 고른 것이다.
 */
const NO_RETRY_CODES: readonly string[] = [UNKNOWN_DOC_TYPE_CODE, INACTIVE_DOC_TYPE_CODE];

/**
 * 기안 만들기.
 *
 * ## ★ `minutesId` 를 실었을 때만 달라지는 것 (2026-09-22 · additive)
 *
 * 그 칸이 없으면 **종전과 한 글자도 다르지 않다** — 한 번 올리고 실패는 그대로 던진다.
 *
 * 실었을 때만 아래가 붙는다:
 * ```
 * minutesLogged      서버가 회의록 이력에 적었나. ⚠ 옛 서버는 «이 칸을 안 준다» → undefined(모른다)
 * minutesLogSkipped  「못 적었다」가 아니라 «아직 적을 때가 아니다»(지금은 'draft' 하나) → 화면은 침묵
 * minutesIdRejected  옛 서버가 그 칸을 몰라 400 → 그 칸만 빼고 다시 올렸다는 뜻
 * ```
 *
 * ## ⚠ 옛 서버 400 을 «한 번» 다시 올린다 — 그러나 «조용히» 빼지 않는다
 *
 * 지금 **운영이 옛 판**이라 이 칸을 실으면 400 이 나고, 그러면 **기안 자체가 안 올라간다.**
 * 「이력이 안 남는 것」보다 「기안이 안 올라가는 것」이 나쁘므로 빼고 다시 올린다
 * (`api/mail.ts` 의 `composeMail` 이 같은 수법을 쓴다 — 배포 순서 결합을 끊는 관용구다).
 *
 * ⛔ 다만 그 선례가 달아 둔 **조건**을 지킨다: 「조용히 빼면 『보냈는데 안 갔다』가 된다」.
 *   여기서 조용히 빼면 **이력이 사라진다** — 되돌리기가 없는 기능에서 이력은 유일한 되짚을 길이다.
 *   그래서 빼고 올린 뒤 **`minutesLogged:false` 로 돌려주어 화면이 반드시 말하게 한다.**
 *
 * ⚠ 400 의 까닭이 이 칸이 아닐 수도 있다(제목 없음 등). 그때는 두 번째 시도도 400 이고 **그 400 을
 *   그대로 올린다.** 400 이면 ajv 단계라 **문서가 만들어지지 않으므로** 다시 올려도 문서가 둘이 되지 않는다.
 *
 * ## ⚠ 운영에서 본 `minutesLogged: false` 는 «고장이 아니었다» (2026-09-22 확인 결과 기록)
 *
 * 확인할 때 결재선을 **`approverIds: []`** 로 올렸기 때문이다. 「나 자신에게만」 보내는 제약 아래서는
 * `["<나>"]` 로 하면 **400 `DRAFTER_IN_LINE`**(기안자를 결재선에 못 넣는다)이라 **`true` 를 만들 길이
 * 없었다.** ⇒ ⛔ 이 값이 `false` 로 나온 것을 「배관 결함」으로 옮겨 적지 말 것.
 * ★ `minutesLogged: true` 는 **아직 한 번도 못 봤다** — 결재자가 따로 있는 기안으로만 확인된다.
 */
export async function createDocument(
  body: CreateDocumentBody,
): Promise<{
  id: string;
  hrRequest?: HrRequestOutcomeDto;
  expenseRequest?: ExpenseOutcomeDto;
  /** 서버가 회의록 이력에 적었나. 옛 서버는 이 칸을 안 준다 → `undefined`(모른다). */
  minutesLogged?: boolean;
  /**
   * ★★★ **「못 적었다」가 아니라 「아직 적을 때가 아니다」** (2026-09-22 · 서버 #1641 · additive).
   *
   * 있으면 화면은 **아무 말도 하지 않는다.** 지금 값은 `'draft'` 하나뿐이다 —
   * 임시저장은 **아직 아무에게도 안 갔으므로 이력이 없는 것이 맞다.**
   *
   * ## ⛔ 이 칸을 걷어내고 `minutesLogged` 를 셋(`'logged'|'skipped'|'failed'`)으로 «합치지 마십시오»
   *
   * 서버(#1641)가 일부러 그렇게 «안» 한 까닭을 그대로 옮긴다:
   *
   * > 값을 셋으로 **안** 바꾼 까닭 — `'logged'|'skipped'|'failed'` 로 바꾸면 8443 의
   * > `=== false` 가 **영영 거짓**이 되어 화면이 **조용히 아무 말도 안 한다.**
   * > (2026-09-22 `used_bytes` 에서 겪은 그 병과 같다.) 그래서 **칸을 더하는 쪽(additive)** 으로 갔다.
   *
   * ★ 즉 `minutesLogged` 의 타입이 `boolean` 인 것은 **게으름이 아니라 계약**이다. 「셋으로 합치면
   *   깔끔한데」로 되돌리면 **읽는 쪽이 조용히 깨진다** — 화면은 아무 말도 안 하는데 시험도
   *   타입도 아무것도 빨개지지 않는다(그것이 이 병의 무서운 점이다).
   * ⚠ 늘릴 때는 «값»을 늘린다 — 「적을 때가 아닌」 다른 까닭이 생기면 `'draft' | '<새 까닭>'`.
   */
  minutesLogSkipped?: 'draft';
  /** 옛 서버가 `minutesId` 를 몰라 그 칸을 빼고 다시 올렸다. */
  minutesIdRejected?: boolean;
}> {
  type Res = {
    id: string; hrRequest?: HrRequestOutcomeDto; expenseRequest?: ExpenseOutcomeDto; minutesLogged?: boolean;
    // ★ 위 반환 타입과 같은 칸 — 서버가 「아직 적을 때가 아니다」로 건너뛴 까닭(지금은 임시저장뿐).
    minutesLogSkipped?: 'draft';
  };
  try {
    const { data } = await apiClient.post<Res>('/approval/documents', body);
    return data;
  } catch (err: unknown) {
    // ★ 그 칸을 안 실었으면 **종전과 똑같이** 던진다 — 기존 동작을 건드리지 않는다.
    if (body.minutesId === undefined) throw err;
    const status = (err as { response?: { status?: number } }).response?.status;
    if (status !== 400) throw err;
    /*
     * ⛔ **뜻이 분명한 업무 거절은 다시 올리지 않는다** (2026-09-22 · 기안 종류 검증 #1626).
     *
     * `UNKNOWN_DOC_TYPE`·`INACTIVE_DOC_TYPE` 은 **`minutesId` 와 무관한 400** 이라 다시 올려도
     * **같은 400** 이 온다 — 결과는 같지만 **쓸데없는 왕복 한 번**이 늘고 사용자는 두 배로 기다린다.
     *
     * ⚠ 「`code` 가 있으면 안 올린다」로는 못 가른다 — **ajv 거절에도 `code` 가 붙는다**
     *   (`server.ts` 의 `setErrorHandler` 마지막 줄이 `reply.send(err)` 로 기본 핸들러에 위임하고,
     *   Fastify 는 검증 오류에 `FST_ERR_VALIDATION` 을 붙인다). 실측하고 알았다.
     * ⇒ 그래서 **아는 것만 막는다**(막을 목록). 모르는 400 은 종전처럼 다시 올린다 —
     *   이 목록이 낡으면 **왕복이 한 번 늘 뿐**이고, 반대로 넓게 막으면 **기안이 안 올라간다.**
     *   틀리는 방향을 고른 것이다.
     */
    if (NO_RETRY_CODES.includes(extractErrorCode(err) ?? '')) throw err;
    const { minutesId: _drop, ...rest } = body;
    const { data } = await apiClient.post<Res>('/approval/documents', rest);
    // ⛔ 조용히 넘어가지 않는다 — 이력이 «안 남았다»는 사실을 화면이 말해야 한다.
    return { ...data, minutesLogged: false, minutesIdRejected: true };
  }
}

/*
  ⛔ `agreeDocument` 를 지웠다 (2026-09-27 · 「합의와 관련된 기능은 다 삭제」).
    서버가 `POST /documents/:id/agree` 를 닫았다(#1774) — 남겨 두면 404 를 부르는 함수가 된다.
*/

export async function approveDocument(
  id: string,
  comment?: string,
): Promise<{ isFinal: boolean; currentStep: number }> {
  const { data } = await apiClient.post<{ isFinal: boolean; currentStep: number }>(
    `/approval/documents/${id}/approve`,
    comment != null ? { comment } : {},
  );
  return data;
}

export async function rejectDocument(
  id: string,
  comment?: string,
): Promise<{ documentId: string; skippedCount: number }> {
  const { data } = await apiClient.post<{ documentId: string; skippedCount: number }>(
    `/approval/documents/${id}/reject`,
    comment != null ? { comment } : {},
  );
  return data;
}

export async function recallDocument(id: string): Promise<{ documentId: string }> {
  const { data } = await apiClient.post<{ documentId: string }>(`/approval/documents/${id}/recall`);
  return data;
}

export async function listDelegates(scope?: 'company'): Promise<ApprovalDelegatesResponse> {
  const { data } = await apiClient.get<ApprovalDelegatesResponse>('/approval/delegates', {
    // ★ 회사 범위일 때만 싣는다 — 옛 서버는 이 칸을 모른다(모르면 그냥 무시한다).
    params: scope ? { scope } : undefined,
  });
  return data;
}

export interface CreateDelegateBody {
  delegateId: string;
  startDate: string;
  endDate: string;
  reason?: string;
}

/**
 * 위임 해지 (결재 A5, 2026-08-24) — **맡긴 사람만** 끌 수 있다.
 *
 * ★ 지우지 않고 끈다. 그 기간에 남이 대신 결재한 사실은 남아야 한다.
 * ★ 옛 서버에는 이 라우트가 없다(404) → 부르는 쪽이 사유를 보여 준다.
 */
export async function revokeDelegate(id: string): Promise<{ id: string; isActive: boolean }> {
  // ⚠ **빈 본문을 그냥 두지 않는다.** `content-type: application/json` 이 붙은 채 본문이 비면
  //   Fastify 가 `FST_ERR_CTP_EMPTY_JSON_BODY` 로 400 을 낸다. 지금 axios 는 본문 없는 PATCH 에
  //   content-type 을 안 붙여서 무사하지만, 전역 기본 헤더가 하나 생기면 그날 깨진다 —
  //   `{}` 를 명시해 그 의존을 없앤다.
  const { data } = await apiClient.patch<{ id: string; isActive: boolean }>(`/approval/delegates/${id}`, {});
  return data;
}

export async function createDelegate(body: CreateDelegateBody): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/approval/delegates', body);
  return data;
}

// ─── 즐겨찾는 결재선 (근태 5단계, 2026-08-24) ───────────────────
//
// "이름은 자유이고 어느 문서에서든 불러온다"(보완 ⑪). 표는 처음부터 있었고 화면이 없었다.

export interface FavoriteLineDto {
  id: string;
  name: string;
  approvers: Array<{ userId: string; name: string | null }>;
  referrers: Array<{ userId: string; name: string | null }>;
  /** 회사를 떠난 사람 수 — 불러오기 전에 알려 준다. */
  missing: number;
  /**
   * ── 공용 즐겨찾기 (2026-08-28 · migration 143) ────────────────────────────
   * ★ 회사가 함께 쓰는 결재선인가. **옛 서버는 안 준다** → 개인 것으로 본다.
   */
  shared?: boolean;
  /**
   * **어느 양식의 기본 결재선인가** (2026-09-04 · 판 P1). 공용 줄에만 값이 있다.
   * ★ 옛 서버는 안 준다 → 지정이 없는 것으로 본다.
   */
  defaultForTypes?: string[];
  /** 누가 만들었나 — 공용에서만 뜻이 있다(내 것은 나다). */
  ownerName?: string | null;
  /**
   * ★★ **지울 수 있는가는 서버가 판정한다.** 화면이 규칙을 다시 쓰면 서버와 갈라진다 —
   *    내 것은 늘, 공용은 회사 관리자만. 옛 서버는 안 준다 → 종전대로 늘 보인다.
   */
  canDelete?: boolean;
}

export async function listFavoriteLines(): Promise<FavoriteLineDto[]> {
  const { data } = await apiClient.get<{ favorites: FavoriteLineDto[] }>('/approval/favorite-lines');
  return Array.isArray(data?.favorites) ? data.favorites : [];
}

/** 같은 이름이면 덮어쓴다 — 같은 이름 둘은 고를 때 구분이 안 된다. */
export async function saveFavoriteLine(body: {
  name: string; approverIds: string[]; referrerIds?: string[];
  /**
   * 공용으로 저장할까 (2026-08-28) — **회사 관리자만** 켤 수 있고 서버가 다시 본다.
   * ★ **켤 때만 싣는다** — 옛 서버는 이 칸을 모르고 `additionalProperties:false` 라 400 이다.
   */
  shared?: boolean;
  /**
   * **이 양식들의 기본 결재선으로 쓴다** (2026-09-04 · 판 P1).
   *
   * ★ 공용 줄에만 뜻이 있다 · 한 양식에 기본은 하나다(서버가 다른 줄에서 그 코드를 뺀다).
   * ★ **값이 있을 때만 싣는다** — 옛 서버는 이 칸을 모르고 `additionalProperties:false` 라 400 이다.
   */
  defaultForTypes?: string[];
}): Promise<{
  id: string; name: string; shared?: boolean;
  defaultForTypes?: string[];
  /** 어느 줄에서 기본을 가져왔나 — 화면이 「‹영업 결재›에서 옮겼습니다」라고 말한다. */
  movedFrom?: string[];
}> {
  const { data } = await apiClient.post('/approval/favorite-lines', body);
  return data;
}

/**
 * **임시보관 상신** (2026-09-04 · 판 P2) — DRAFT → 진행 중.
 *
 * ★ 결재선은 **여기서** 확정된다 — 임시보관은 빈 채로도 저장되지만 상신은 결재 1명이 있어야 한다.
 * ★ 옛 서버에는 이 주소가 없다(404) — 부르는 쪽이 그 사실을 말한다.
 */
export async function submitDraft(
  id: string,
  // 만드는 곳은 `lib/approval-submit-body.ts` 한 곳이다(결재자 거르개가 거기 있다).
  body: { approverIds: string[]; referrerIds?: string[] },
): Promise<{ id: string; hrRequest?: HrRequestOutcomeDto; expenseRequest?: ExpenseOutcomeDto }> {
  const { data } = await apiClient.post(`/approval/documents/${id}/submit`, body);
  return data;
}

/**
 * **임시보관 삭제** (판 P2) — 아무에게도 안 갔으므로 흔적을 남길 이유가 없다.
 *
 * ★ 상신된 문서는 지워지지 않는다(서버가 막는다) — 그건 「회수」로 남는다.
 */
export async function deleteDraft(id: string): Promise<{ ok: boolean }> {
  const { data } = await apiClient.delete(`/approval/documents/${id}`);
  return data;
}

export async function deleteFavoriteLine(id: string): Promise<void> {
  await apiClient.delete(`/approval/favorite-lines/${id}`);
}

// ── 결재 현황 (2026-08-31 · 시안 맞춤 5/9) ────────────────────────────────

export interface ApprovalStatsDto {
  period: 'week' | 'month' | 'quarter';
  from: string;
  to: string;
  /** 몇 일부터 「지연」으로 세나 — 서버가 정한다(화면이 규칙을 다시 쓰지 않는다). */
  delayDays: number;
  summary: {
    drafted: number;
    approved: number;
    rejected: number;
    /** 완료가 없으면 `null` — **0일이 아니다**. */
    avgDays: number | null;
    delayed: number;
    /** 판정된 것이 없으면 `null`. */
    rejectRate: number | null;
  };
  types: Array<{
    type: string; count: number; avgDays: number | null; delayed: number;
    /**
     * ★★★ **어느 단계에서 오래 서나** (2026-08-31 · 시안 F1). 옛 서버에는 없다 → `undefined`.
     * ★ 단계에 **이름을 붙이지 않는다** — 줄에는 번호만 있고, 그때 그 사람이 팀장이었는지
     *   대표였는지는 어디에도 안 남는다. 지금 직책으로 적으면 «그때»가 아니라 «지금»이 된다.
     */
    steps?: Array<{ step: number; avgDays: number; count: number }>;
  }>;
  holders: Array<{ userId: string; name: string; count: number; avgDays: number | null }>;
  stale: Array<{ id: string; title: string; type: string; holderName: string | null; days: number }>;
}

export async function getApprovalStats(period: 'week' | 'month' | 'quarter'): Promise<ApprovalStatsDto> {
  const { data } = await apiClient.get<ApprovalStatsDto>('/approval/stats', { params: { period } });
  return data;
}

/**
 * **문서 이력** (2026-09-04 · 판 P6 · 결정 ⑧) — 「이 문서에 무슨 일이 있었나」.
 *
 * ★★ 표를 새로 만들지 않았다 — **감사 로그가 정본**이다(해시 사슬 · 고칠 수 없다).
 * ★★ 승인·반려는 감사에 «줄» 로 적혀 있어 서버가 문서·줄 둘 다 읽는다 —
 *    한쪽만 읽으면 **승인이 통째로 빠지고** 화면이 「아무 일도 없었다」로 그린다.
 * ★ 옛 서버는 이 주소가 없다(404) → 부르는 쪽이 탭을 접는다.
 */
export interface HistoryEntryDto {
  id: string;
  at: string;
  eventType: string;
  actorName: string | null;
  /** 대신 눌린 사람 — 위임이 아니면 `null`. */
  onBehalfOfName: string | null;
  step: number | null;
  comment: string | null;
}

export async function fetchDocumentHistory(id: string): Promise<HistoryEntryDto[]> {
  const { data } = await apiClient.get<{ entries: HistoryEntryDto[] }>(`/approval/documents/${id}/history`);
  return data.entries ?? [];
}
